import React from 'react';
import { Alert, Spin, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTransformationStatus, getAlertType } from '../../hooks/useTransformationStatus';

/**
 * Props for TransformationStatus component
 */
interface TransformationStatusProps {
  /** Name of the data collection (e.g., "c001_data_coll_2023") */
  dataCollName: string;
  /** Client authentication information */
  client: { id: string; token: string };
  /** Optional custom styles */
  style?: React.CSSProperties;
  /** Whether a transformation was just triggered by the user */
  transformationTriggered: boolean;
  /** Timestamp when the transformation was triggered (used for stale detection) */
  transformationTriggeredAt: Date | null;
  /** Whether the basis_upload table exists for this data collection */
  hasBasisUploadTable?: boolean;
  /** Callback when transformation status changes */
  onStatusChange?: (status: any) => void;
}

/**
 * TransformationStatus Component
 *
 * Displays transformation status and manages polling.
 *
 * **Display Logic:**
 * - Shows when `transformationTriggered=true` OR `hasBasisUploadTable=true`
 *
 * **Stale Detection:**
 * - With `transformationTriggered=true`: Only shows statuses created after trigger time
 * - Otherwise: Shows any status (no filtering)
 *
 * **Polling:**
 * - Polls every 10s, stops on terminal status (completed/failed/error)
 * - Restarts when transformation is triggered
 */
const TransformationStatus: React.FC<TransformationStatusProps> = ({
  dataCollName,
  client,
  style,
  transformationTriggered,
  transformationTriggeredAt,
  hasBasisUploadTable,
  onStatusChange,
}) => {
  // Only use timestamp filtering when actively viewing a newly triggered transformation
  // When just viewing existing status (hasBasisUploadTable but not transformationTriggered),
  // don't filter by timestamp
  const timestampToUse = transformationTriggered ? transformationTriggeredAt : null;

  const { transformationStatus, statusLoading, startPolling, stopPolling, manualRefresh } =
    useTransformationStatus({
      dataCollName,
      client,
      transformationTriggeredAt: timestampToUse,
      onStatusChange,
    });

  const shouldShowStatus = transformationTriggered || hasBasisUploadTable;

  // Start/restart polling when conditions change
  React.useEffect(() => {
    if (shouldShowStatus) {
      console.log(
        'Starting polling - transformationTriggered:',
        transformationTriggered,
        'timestampToUse:',
        timestampToUse?.toISOString()
      );
      startPolling();
    } else {
      console.log('Stopping polling - shouldShowStatus is false');
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [shouldShowStatus, timestampToUse, transformationTriggered, startPolling, stopPolling]);
  if (!shouldShowStatus) {
    return null;
  }

  if (statusLoading && !transformationStatus) {
    return (
      <Alert
        type="info"
        showIcon
        message={
          <Space>
            <Spin size="small" />
            Lade Transformationsstatus...
          </Space>
        }
        style={style}
      />
    );
  }

  if (!transformationStatus) {
    return null;
  }

  const alertType = transformationStatus.error
    ? 'error'
    : getAlertType(transformationStatus.status);

  return (
    <Alert
      type={alertType}
      showIcon
      message={
        <Space>
          {statusLoading && <Spin size="small" />}
          {transformationStatus.error
            ? 'Fehler beim Abruf des Transformationsstatus'
            : 'Status der Transformation'}
        </Space>
      }
      description={
        transformationStatus.error ? (
          transformationStatus.error
        ) : (
          <>
            <b>Stammdatentabelle:</b> {`${dataCollName}_basis_upload`}
            <br />
            <b>Status:</b> {transformationStatus.status}
            <br />
            {transformationStatus.message && (
              <>
                <b>Statustext:</b> {transformationStatus.message}
                <br />
              </>
            )}
            {transformationStatus.processing_details?.error_message && (
              <>
                <b>Fehlerdetails:</b> {transformationStatus.processing_details.error_message}
                <br />
              </>
            )}
            {transformationStatus.calculation_status?.calc_status_message && (
              <>
                <b>Berechnungsstatus:</b>{' '}
                {transformationStatus.calculation_status.calc_status_message}
                <br />
              </>
            )}
            {transformationStatus.current_transformation?.created_at && (
              <>
                <b>Transformationsbeginn:</b>{' '}
                {new Date(transformationStatus.current_transformation.created_at).toLocaleString(
                  'de-DE'
                )}
                <br />
              </>
            )}
            {transformationStatus.current_transformation?.updated_at && (
              <>
                <b>Letzte Statusaktualisierung:</b>{' '}
                {new Date(transformationStatus.current_transformation.updated_at).toLocaleString(
                  'de-DE'
                )}
                <br />
              </>
            )}
            {transformationStatus.calculation_status?.calc_finished_at && (
              <>
                <b>Berechnung abgeschlossen:</b>{' '}
                {new Date(transformationStatus.calculation_status.calc_finished_at).toLocaleString(
                  'de-DE'
                )}
                <br />
              </>
            )}
            {alertType === 'success' &&
              '\nDer Upload Ihrer Stammdaten wurde erfolgreich verarbeitet und wird bei der nächsten Neuberechnung Ihres Berichts berücksichtigt.'}
          </>
        )
      }
      action={
        manualRefresh && (
          <Button
            icon={<ReloadOutlined />}
            onClick={manualRefresh}
            loading={statusLoading}
            type="default"
            size="small"
          >
            Aktualisieren
          </Button>
        )
      }
      style={{ whiteSpace: 'pre-wrap', ...style }}
    />
  );
};

export default TransformationStatus;
